import { useMutation } from '@tanstack/react-query'
import { CHAIN_CONFIG, CHAIN_INFO_LIST } from '@/config'
import {
  simulate,
  calculateFeeWithHelper,
  deductFeeFromTxAmountWhileEmptyStrd,
  generateIbcTimeoutTimestampBigInt
} from '@/wallet-utils'
import { assert } from '@/utils'
import { MutationParameters } from './types'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { useSelectedDenom } from '@/contexts/denom'
import { useSelectedChain, useStrideChain, useStrideChainBalance } from '@/contexts/wallet'
import { ibc } from 'stridejs'

export interface SignWithdrawStTokenMutationReturnType {
  raw: TxRaw

  // For this flow, rate limiting may affect the amount that was actually sent.
  // If the amount being withdrawn only exceeds the capacity of the chain, we send the
  // capacity of the chain. If the transaction worked, the simplest and most reliable way
  // to get the amount in the transaction is by simply storing it into a state.
  //
  // To elaborate, actual IBC is slow. On top of this, the actual IBC experience is even
  // way slower. Given that rate limiting query is refetched more oftenly, it's possible that by then
  // capacity is already zero.
  amount: string
}

const useSignWithdrawStTokenMutation = ({ setWithdrawStTokenRaw }: MutationParameters) => {
  const [denom] = useSelectedDenom()

  const { address: strideAddress, getSigningStargateClient } = useStrideChain()

  const { address: selectedAddress } = useSelectedChain()

  const { data: strideBalances } = useStrideChainBalance()

  const handleMutation = async (amount: string): Promise<SignWithdrawStTokenMutationReturnType> => {
    assert(strideAddress, 'Uanble to send st tokens out of Stride without connecting your Stride wallet.')

    assert(selectedAddress, `Unable to send st tokens out of Stride without connecting your ${denom} wallet.`)

    assert(strideBalances, `Unable to send st tokens out of Stride without your Stride wallet balances.`)

    const minimalDenom = CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom

    const stakedDenom = `st${minimalDenom}`

    const createMsgTransfer = (amount: string) => {
      return ibc.applications.transfer.v1.MessageComposer.withTypeUrl.transfer({
        sourcePort: 'transfer',
        sourceChannel: CHAIN_CONFIG[denom].withdrawChannel,
        token: {
          amount,
          denom: stakedDenom
        },
        sender: strideAddress,
        receiver: selectedAddress,
        memo: '',
        timeoutHeight: {
          revisionHeight: 0n,
          revisionNumber: 0n
        },
        timeoutTimestamp: generateIbcTimeoutTimestampBigInt()
      })
    }

    const msgTransferForSimulation = createMsgTransfer(amount)
    const client = await getSigningStargateClient()
    const gas = await simulate(client, strideAddress, [msgTransferForSimulation], '')
    const { fee, deductible } = calculateFeeWithHelper(minimalDenom, gas)

    // Allot a small portion of the user's tokens for the transaction gas fee
    //
    // Testing Guide (Preparation):
    // 1. Setup a fresh account, and send a small amount of tokens to it (e.g., 1 SAGA).
    // 2. Liquid Stake
    // 3. Proceed with "Withdraw St Token" flow.
    // 4. Inspect the transaction amount and you'll find that it should be less than the full amount.
    //
    // @TODO: Only deduct if the user does not have enough tokens for gas
    const amountWithSafetyDeduction = deductFeeFromTxAmountWhileEmptyStrd(
      strideBalances.balances,
      BigInt(amount),
      deductible
    )

    const msgTransfer = createMsgTransfer(amountWithSafetyDeduction.toString())

    return {
      raw: await client.sign(strideAddress, [msgTransfer], fee, ''),
      amount
    }
  }

  const handleSuccess = ({ raw }: SignWithdrawStTokenMutationReturnType) => {
    setWithdrawStTokenRaw(raw)
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignWithdrawStTokenMutation }
